import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import LegalShell, { Section, P, UL, Notice } from '../components/LegalShell';
import { BRAND_NAME } from '../utils/brand';

//
// /sensitive-data-consent — Explicit consent for collecting sensitive
// personal data (health information).
//
// Source: Website Build Handoff V1.0:
//   • sec. 3 Sitemap — "Mandatory before collecting health data"
//   • sec. 4 Conversion Funnel — consent gate before assessment
//   • sec. 9 Compliance Guardrails
//   • sec. 6 Audit trail (consent version + timestamp must be logged
//     when the user accepts via the assessment flow)
//
// This page is the *informational* version of the consent. The actual
// machine-recorded acceptance happens at the assessment consent gate
// (Phase D). The two MUST reference the same version string.
//
// Compliance note: requires sign-off from legal / data privacy lead
// before launch.
//

const CONSENT_VERSION = '1.0 (Draft — pending legal and data-privacy review)';

export default function SensitiveDataConsent() {
  useEffect(() => {
    const prev = document.title;
    document.title = `Sensitive Personal Data Consent | ${BRAND_NAME}`;
    return () => { document.title = prev; };
  }, []);

  return (
    <LegalShell
      eyebrow="Patient consent"
      title="Sensitive Personal Data Consent"
      intro={
        `${BRAND_NAME} needs to collect and process certain sensitive personal ` +
        `data — your health information — to provide AI-assisted assessment, ` +
        `licensed doctor consultation, pharmacy coordination and follow-up ` +
        `support. This page explains what we collect, why, who can see it and ` +
        `your rights. You must accept this consent before sharing any health ` +
        `information through the platform.`
      }
      version={CONSENT_VERSION}
      lastUpdated="June 2026"
      governingLaw="Malaysia"
    >
      <Section id="why" number={1} heading="Why we ask for this consent">
        <P>
          Under the Malaysian Personal Data Protection Act 2010 (PDPA),
          information about your physical or mental health is treated as
          <em> sensitive personal data</em>. We are not allowed to collect or
          use this information without your explicit consent.
        </P>
        <P>
          {BRAND_NAME} is operated by <strong>Melonco Sdn. Bhd.</strong>, a
          healthcare technology and care coordination platform. We use the
          information you share to facilitate access to licensed healthcare
          providers and licensed pharmacy partners — we do not ourselves
          diagnose, prescribe or dispense medication.
        </P>
      </Section>

      <Section id="data" number={2} heading="What sensitive data we may collect">
        <P>
          As part of the AI-assisted health assessment and any subsequent
          consultation or follow-up, you may share information including:
        </P>
        <UL>
          <li>Height, weight, BMI and waist circumference</li>
          <li>Weight history, target weight and weight-loss goals</li>
          <li>Diagnoses such as diabetes, hypertension, thyroid disease, kidney disease, gallbladder disease, GI disease, pancreatitis history, retinopathy, eating-disorder history</li>
          <li>Current medications, supplements and prior GLP-1 / weight-loss medication use</li>
          <li>Allergies and previous reactions to medication</li>
          <li>Pregnancy, breastfeeding or pregnancy-planning status</li>
          <li>Upcoming surgery, endoscopy or procedures requiring anaesthesia / deep sedation</li>
          <li>Symptoms you report through the assessment, follow-ups or messaging</li>
          <li>Lifestyle information — eating pattern, activity, sleep, stress, work schedule</li>
          <li>Photographs you choose to upload as part of progress tracking (optional)</li>
          <li>Doctor notes, prescriptions and dispensing records shared by your provider or pharmacy</li>
        </UL>
        <P>
          We collect only what is needed for the purpose described in each
          step. You can choose not to provide some details, but this may limit
          the assessment, consultation or treatment that can be offered.
        </P>
      </Section>

      <Section id="purposes" number={3} heading="Why we use your sensitive data">
        <P>We use your sensitive personal data to:</P>
        <UL>
          <li>Run the AI-assisted health assessment and eligibility pre-screening</li>
          <li>Make your information available to a licensed healthcare provider for medical review and consultation</li>
          <li>Coordinate medication fulfilment with licensed pharmacy partners <strong>only</strong> where a prescription has been issued by a licensed doctor</li>
          <li>Provide AI-assisted reminders, progress tracking, education and follow-up support</li>
          <li>Detect safety signals, contraindications and urgent symptoms that need escalation</li>
          <li>Improve the safety and quality of the platform, using de-identified data where possible</li>
          <li>Comply with applicable healthcare, regulatory and legal obligations</li>
        </UL>
        <Notice tone="info">
          {BRAND_NAME} will not sell your sensitive personal data. We will not
          use it for advertising. We will not share it with insurers or
          employers without your explicit, separate consent.
        </Notice>
      </Section>

      <Section id="who-sees" number={4} heading="Who may see your sensitive data">
        <P>Your sensitive personal data may be shared with:</P>
        <UL>
          <li>Licensed healthcare providers who review your assessment and conduct your consultation</li>
          <li>Licensed pharmacy partners, only where you have been prescribed medication and only as needed to dispense and counsel safely</li>
          <li>{BRAND_NAME} staff who support clinical operations, escalations and care coordination, under confidentiality obligations</li>
          <li>Technology service providers that host or process data on our behalf, under contractual safeguards</li>
          <li>Regulators or authorities, where required by law</li>
        </UL>
      </Section>

      <Section id="ai" number={5} heading="How AI is used with your sensitive data">
        <P>
          {BRAND_NAME} uses AI to support intake, screening, education,
          reminders and progress tracking. The AI is <strong>not</strong> a
          doctor. It does not diagnose, prescribe, replace emergency care or
          override a licensed healthcare provider's judgement.
        </P>
        <P>
          We design the AI assessment so that medical decisions — including
          eligibility for prescription treatment — remain with a licensed
          doctor. Where AI is used to flag safety concerns (for example, a
          potential contraindication or a red-flag symptom), the result is
          either escalated to a healthcare provider or shown as guidance for
          you to seek care.
        </P>
      </Section>

      <Section id="security" number={6} heading="How we protect your sensitive data">
        <UL>
          <li>Encryption in transit (HTTPS / TLS) and at rest for sensitive data stores</li>
          <li>Role-based access controls — staff only see what they need</li>
          <li>Logging and audit trails for sensitive data access</li>
          <li>Backups and disaster recovery procedures</li>
          <li>Vendor due diligence and contractual safeguards on third-party processors</li>
        </UL>
        <P>
          No system is perfectly secure. If you become aware of a security
          concern, please contact us immediately through the{' '}
          <Link to="/contact" className="text-brand-700 underline hover:no-underline">
            Contact page
          </Link>.
        </P>
      </Section>

      <Section id="retention" number={7} heading="How long we keep it">
        <P>
          We retain your sensitive personal data for as long as you maintain an
          active account with us, and for any further period required by
          applicable healthcare record-keeping, tax, regulatory or legal
          obligations. Where the law allows, we may keep de-identified
          information for safety, quality and research purposes.
        </P>
      </Section>

      <Section id="rights" number={8} heading="Your rights">
        <P>You have the right, subject to applicable law, to:</P>
        <UL>
          <li>Access the personal data we hold about you</li>
          <li>Correct inaccurate or incomplete information</li>
          <li>Withdraw this consent at any time (see section 9)</li>
          <li>Request limitation of certain processing</li>
          <li>Lodge a complaint with the Personal Data Protection Commissioner of Malaysia</li>
        </UL>
        <P>
          Full details of how we handle personal data are in our{' '}
          <Link to="/privacy" className="text-brand-700 underline hover:no-underline">
            Privacy Policy
          </Link>.
        </P>
      </Section>

      <Section id="withdraw" number={9} heading="Withdrawing this consent">
        <P>
          You can withdraw this consent at any time by contacting us through
          the{' '}
          <Link to="/contact" className="text-brand-700 underline hover:no-underline">
            Contact page
          </Link>{' '}
          or by emailing the data-privacy address listed in our Privacy Policy.
        </P>
        <Notice tone="warning">
          If you withdraw consent, {BRAND_NAME} will stop processing your
          sensitive personal data for the purposes described above and will not
          be able to continue providing services that depend on it (for
          example, doctor consultations and follow-up support). Healthcare
          providers and pharmacies may need to retain records they are legally
          required to keep about care or medication already provided to you.
        </Notice>
      </Section>

      <Section id="children" number={10} heading="Use by minors">
        <P>
          {BRAND_NAME}'s weight-loss services are intended for adults.
          Individuals under 18 must not submit an assessment or accept this
          consent on their own.
        </P>
      </Section>

      <Section id="how-to-accept" number={11} heading="How you accept this consent">
        <P>
          When you start the AI-assisted health assessment, you will be asked
          to confirm that you have read, understood and agree to this Sensitive
          Personal Data Consent, our{' '}
          <Link to="/privacy" className="text-brand-700 underline hover:no-underline">
            Privacy Policy
          </Link>{' '}
          and our{' '}
          <Link to="/terms" className="text-brand-700 underline hover:no-underline">
            Terms &amp; Conditions
          </Link>.
        </P>
        <P>
          We will record the consent version, the date and time you accepted,
          and a record of the device used, to maintain an audit trail of your
          consent. You can ask us for a copy of your consent record at any
          time.
        </P>
      </Section>

      <Section id="contact" number={12} heading="Questions about this consent">
        <P>
          If anything on this page is unclear, please contact us through the{' '}
          <Link to="/contact" className="text-brand-700 underline hover:no-underline">
            Contact page
          </Link>{' '}
          before accepting. Do not submit sensitive health information until
          you are comfortable with how it will be used.
        </P>
      </Section>
    </LegalShell>
  );
}
